/**
 * Skill DSL / AST types.
 *
 * A skill is a small, *constrained* tree of primitives. There is deliberately
 * no `eval`, `script`, `formula`, or arbitrary-class-spawn construct — the AI
 * layer (added later) may only emit these typed nodes, never executable code.
 * See deep-research-report.md §"엔진 아키텍처·DSL·안전성".
 */

/**
 * MASTERY.1 (owning this vocabulary — see src/calibration/skill-damage-v13.ts,
 * which re-exports this type for compatibility): the closed generation-time
 * growth-profile vocabulary. Do not maintain a second `'current'|'normal'|'growth'`
 * union anywhere else.
 */
export type SkillGrowthProfile = 'current' | 'normal' | 'growth';
export const SKILL_GROWTH_PROFILES: readonly SkillGrowthProfile[] = ['current', 'normal', 'growth'];

export const SKILL_MASTERY_POLICY_SCHEMA_VERSION = 'skill-mastery-realization-v1' as const;

/**
 * MASTERY.1 canonical, immutable authored Mastery-realization metadata for a
 * supported canonical direct-damage Skill. `rawManaBeforeMastery` is the
 * unrounded mana basis AFTER every current non-Mastery raw adjustment
 * (focus/MagicInfluence) and BEFORE Mastery realization and the single final
 * integer round (see src/calibration/mana-damage-v1.ts
 * `rawManaBeforeMastery()` / `realizedManaCostFromMasteryBasis()`). Runtime
 * MUST NOT recompute q/cadence/ReferenceBasis/focus/MagicInfluence pricing
 * from this value — it is a pure realization-boundary input.
 */
export interface SkillMasteryPolicyV1 {
  readonly schemaVersion: typeof SKILL_MASTERY_POLICY_SCHEMA_VERSION;
  readonly growthProfile: SkillGrowthProfile;
  readonly rawManaBeforeMastery: number;
}

export const DIRECT_DAMAGE_AUTHORING_PROVENANCE_SCHEMA_VERSION = 'direct-damage-authoring-provenance-v1' as const;

/**
 * Plan `sparkling-purring-allen` Stage 3 — a read-only summary of the
 * crafter's `SourceSnapshot` at the moment this skill's F/K was decomposed
 * against it (authoring anchor = crafter, not a fixed standard character).
 * Diagnostic/traceability only: runtime must never re-derive damage from
 * this, and F/K + `sourceRequirement` remain the sole runtime damage
 * authority. Exists to compensate for the partial path-independence loss
 * that anchor shift accepts — the same requested numbers can now produce a
 * different F/K for different crafters, so this keeps "what was I holding
 * when I made this" attached to the skill itself.
 */
export interface DirectDamageAuthoringProvenanceV1 {
  readonly schemaVersion: typeof DIRECT_DAMAGE_AUTHORING_PROVENANCE_SCHEMA_VERSION;
  readonly hBase: number;
  readonly hStat: number;
  readonly family: WeaponFamily;
  /** V13 continuous growth-profile axis position (`skill-damage-v13.ts`'s `V13_PROFILE_T`), not a re-derivable literal. */
  readonly t: number;
  /**
   * The profile `damageSlope` the skill's F/K were authored against. F/K weight the hit's base and stat parts,
   * so their meaning depends on it. Absent on every skill stored before the single 3% slope: those were authored
   * at `LEGACY_AUTHORING_DAMAGE_SLOPE` (0.003) and execute with that split (`withAuthoredDamageSplit`).
   */
  readonly damageSlope?: number;
}

export const NON_DAMAGE_PLACEHOLDER_AUTHORING_SCHEMA_VERSION = 'non-damage-placeholder-authoring-v1' as const;

/**
 * Effect kinds this placeholder authoring path is allowed to size. See
 * `PlaceholderNonDamageComponentEvidence`. `dot` was removed here by the B
 * track (dot-as-damage,
 * `docs/superpowers/plans/2026-09-10-dot-as-damage-authoring.md`): dot is
 * damage-bearing (`DAMAGE_BEARING_EFFECT_KINDS`) and authors through the real
 * F/K-equivalent damage pipeline now, never this budget-% placeholder.
 */
export const PLACEHOLDER_NON_DAMAGE_EFFECT_KINDS = ['stun', 'root', 'slow', 'knockback', 'pull'] as const;
export type PlaceholderNonDamageEffectKind = (typeof PLACEHOLDER_NON_DAMAGE_EFFECT_KINDS)[number];

/** The effects a share of a cast's power is solved into: the placeholder kinds' magnitudes, and an element status's potency. */
export const IN_POWER_EFFECT_KINDS = [...PLACEHOLDER_NON_DAMAGE_EFFECT_KINDS, 'element_status'] as const;
export type InPowerEffectKind = (typeof IN_POWER_EFFECT_KINDS)[number];

/**
 * NOT AN AUTHORITATIVE BALANCE DECISION. `src/generation/numeric/` owns no
 * real magnitude-authoring logic for any non-damage effect kind yet (only
 * `damage`'s F/K path is real, calibrated authority) — this evidence marks a
 * component whose duration/distance was instead derived by a bootstrap
 * placeholder (`src/generation/numeric/placeholder-non-damage-authoring.ts`)
 * that crudely inverts `src/eval/effects.ts`'s pricing formula against a
 * fixed budget share, so hand-authored experiment skills with bounded
 * offensive CC or DoT riding a damage component (BETA.SCOPE.2) can reach
 * `SOLVED` instead of unconditionally deferring. `magnitudeAuthority` is the
 * machine-checkable flag: a consumer MUST treat `'PROVISIONAL_UNCALIBRATED'`
 * components as not balance-authoritative — this field exists specifically
 * so a bootstrap number is never again silently mistaken for a ratified one.
 */
export interface PlaceholderNonDamageComponentEvidence {
  readonly schemaVersion: typeof NON_DAMAGE_PLACEHOLDER_AUTHORING_SCHEMA_VERSION;
  readonly componentId: string;
  readonly kind: InPowerEffectKind;
  readonly magnitudeAuthority: 'PROVISIONAL_UNCALIBRATED';
  readonly budgetSpentSVP: number;
}

/**
 * The closed Beta V1 Mechanical Damage Type vocabulary
 * (beta-skill-fantasy-envelope-contract.md §8.1) — a semantic axis
 * orthogonal to Status (FIRE does not imply Burn, etc; contract §9). Exactly
 * these six values; no arbitrary/free-form/extra member may be added without
 * a versioned product-contract amendment (contract §5.1).
 *
 * "True damage" (mitigation bypass) is deliberately NOT a seventh member
 * here — it was always a distinct, orthogonal axis at the runtime layer
 * (`combat.ts`'s `mitigationMode`, independent of `damageClass`/`attribute`)
 * and is represented on `DamageEffect` as `trueDamage?: boolean` instead. See
 * phase-dt1-mechanical-damage-type-audit.md "Design Decision D1".
 */
export type MechanicalDamageType = 'physical' | 'fire' | 'cold' | 'lightning' | 'toxic' | 'arcane';

export const MECHANICAL_DAMAGE_TYPES: readonly MechanicalDamageType[] = [
  'physical',
  'fire',
  'cold',
  'lightning',
  'toxic',
  'arcane',
];

/** The only `LeafEffect` kinds that can reduce target HP (direct or periodic) — the exhaustive
 * damage-bearing classification DT.1 audit §D establishes as the single source of truth, reused
 * by both `SkillIntent.semantic.damageTypeBindings` and `src/safety.ts` validation. */
export const DAMAGE_BEARING_EFFECT_KINDS = ['damage', 'dot'] as const;
export type DamageBearingEffectKind = (typeof DAMAGE_BEARING_EFFECT_KINDS)[number];

export type ResourceKind = 'mana' | 'energy' | 'hp';

/**
 * ACTIVE.1 — the closed V0 Weapon Family vocabulary
 * (weapon-primary-basic-attack-contract.md §3). Equipment/runtime category
 * used by basic attacks, stat affinity, handling, and Reference Weapon
 * fixtures — distinct from `SkillCombatClass` (MELEE/RANGED/MAGIC), which
 * this phase does not add as separate schema (see the ACTIVE.1 audit's
 * "Implementation boundary" section: no additional legality/runtime
 * consequence exists for it in this narrow vertical slice). No free-form
 * family names.
 */
export type WeaponFamily = 'HEAVY' | 'BALANCED' | 'LIGHT' | 'POLEARM' | 'RANGED' | 'CATALYST';

export const WEAPON_FAMILIES: readonly WeaponFamily[] = ['HEAVY', 'BALANCED', 'LIGHT', 'POLEARM', 'RANGED', 'CATALYST'];

/**
 * SOURCE.2 — the closed Source Class vocabulary
 * (source-weapon-technique-hybrid-contract.md §18). Never derived from
 * `MechanicalDamageType` (FIRE/ARCANE alone never implies MAGIC; PHYSICAL
 * alone never implies WEAPON).
 */
export type SourceClass = 'BODY' | 'WEAPON' | 'MAGIC';

export const SOURCE_CLASSES: readonly SourceClass[] = ['BODY', 'WEAPON', 'MAGIC'];

/**
 * SOURCE.2 — the closed Beta V1 weapon-technique vocabulary
 * (source-weapon-technique-hybrid-contract.md §19). Intentionally smaller
 * than fantasy language; `CAST` is deliberately absent — MAGIC is
 * represented by `SourceClass`, not a fake weapon capability.
 */
export type WeaponTechnique = 'SWING' | 'THRUST' | 'SHOOT';

export const WEAPON_TECHNIQUES: readonly WeaponTechnique[] = ['SWING', 'THRUST', 'SHOOT'];

/**
 * SOURCE.2 — magic equipment influence (source-weapon-technique-hybrid-contract.md
 * §23). Independent from growth profile and Mechanical Damage Type.
 */
export type MagicInfluence = 'LOW' | 'MEDIUM' | 'HIGH';

export const MAGIC_INFLUENCES: readonly MagicInfluence[] = ['LOW', 'MEDIUM', 'HIGH'];

/**
 * ACTIVE.1 / SOURCE.2 — source requirement legality (active-skill-mechanics-addendum-v0.1.md
 * §4). A
 * discriminated union over `SourceClass`:
 *
 * - `BODY`/`MAGIC` carry no technique/family — BODY numeric realization is
 *   deferred (source-weapon-technique-hybrid-contract.md §20); MAGIC always
 *   resolves at runtime through a Catalyst provider or the universal
 *   intrinsic MAGIC fallback (§21).
 * - `WEAPON` always carries a `technique`; `families`, when present, is an
 *   *explicit* player restriction only — absent means all
 *   technique-compatible families are legal (generic compatibility is now
 *   technique-owned, never a `families: all six` list). Legality only;
 *   narrower requirements never receive specialization power credit (Issue
 *   #14 remains open).
 */
export type SourceRequirement =
  | { readonly sourceClass: 'BODY' }
  | { readonly sourceClass: 'MAGIC' }
  | {
      readonly sourceClass: 'WEAPON';
      readonly technique: WeaponTechnique;
      /** Explicit user restriction only; absent means all compatible families. */
      readonly families?: readonly WeaponFamily[];
    };

/**
 * SOURCE.1 — the closed Reference Basis vocabulary
 * (skill-generation-reference-basis-contract.md,
 * source-weapon-technique-hybrid-contract.md §3): answers "what standardized
 * source is this numeric skill defined relative to?" at generation time.
 * Deliberately orthogonal to `SourceRequirement` (eligibility, this file),
 * `MechanicalDamageType` (mechanical damage identity, above), and magical
 * augmentation (deferred) — none of those axes may be derived from this one
 * or vice versa. `WEAPON reference + ARCANE/FIRE` and `MAGIC reference +
 * ARCANE/FIRE` must both remain representable without conflation.
 */
export type ReferenceBasis = 'WEAPON' | 'MAGIC';

export const REFERENCE_BASES: readonly ReferenceBasis[] = ['WEAPON', 'MAGIC'];

/**
 * Canonical runtime availability fact persisted by authoring. It contains no
 * q/reference/calibration coordinates: runtime consumes the already-resolved
 * policy and never reconstructs generation authority from F/K or live source
 * state.
 */
export type CanonicalAvailabilityPolicy =
  | { readonly kind: 'SOURCE_RELATIVE_ONLY' }
  | { readonly kind: 'ORDINARY_COOLDOWN_FLOOR'; readonly minimumIntervalS: number }
  | {
      readonly kind: 'ROLLING_CHARGE_FLOOR';
      readonly minimumIntervalS: number;
      readonly capacity: 1;
      readonly rollingRechargeS: number;
    };

/**
 * ACTIVE.1 — the source-relative A/C timing authority, amended by SOL.3A-3
 * runtime-availability closure and ACTIVE.2's windup split. The total
 * action lock is `A × actionCycleS`; `windupFraction` splits that total into
 * a pre-release startup (`windupFraction × A × actionCycleS`) and a
 * post-release recovery (`(1 − windupFraction) × A × actionCycleS`), so the
 * total is preserved exactly. Raw recast is `C × recastCycleS`;
 * `availability` then resolves the final legal runtime interval.
 * Source-relative handling may make recast slower, never faster than an
 * authored absolute floor.
 *
 * The windup split lives entirely inside this object: `Skill.castTimeS` /
 * `recoveryS` stay forbidden alongside `sourceRelativeTiming`, so
 * ACTIVE.1's single-timing-authority invariant is unchanged. `src/safety.ts`
 * validates the combination and the `windupFraction ∈ [0, 1]` range.
 *
 * Mutually exclusive with `Skill.cooldownS` and with
 * `castTimeS`/`recoveryS`/`charges`/`chargeRechargeS` — presence
 * establishes authority.
 */
export interface SourceRelativeTiming {
  actionCycles: number;
  recastCycles: number;
  /**
   * ACTIVE.2 — pre-release fraction of the `A × actionCycleS` action lock, in
   * `[0, 1]`. Absent or `0` reproduces ACTIVE.1's immediate-release behaviour
   * byte for byte (all lock is post-release recovery). Canonical generation
   * always emits it explicitly; hand-authored ACTIVE.1 fixtures may omit it.
   * A q-derived curve (`resolveWindupFraction`), never an authored choice in
   * this phase.
   */
  windupFraction?: number;
  availability: CanonicalAvailabilityPolicy;
}

/**
 * The §32.5 "Scaling Hook" vocabulary (skill-value-engine-contract.md §32).
 * Each value names one Derived Stat / Runtime Mechanic category a leaf
 * effect may declare a relationship to (§32.2) — e.g. "this damage effect's
 * magnitude conceptually depends on impact speed", not a coefficient or a
 * Primary-stat reference. These are semantic tokens only: no Primary→Derived
 * mapping, formula, or runtime resolution exists yet for any of them (that
 * is future Runtime Stat System scope, not this phase — see the G.2
 * completion doc). A leaf with no `scalingHooks` has no declared
 * relationship, exactly as before this field existed.
 */
export type ScalingHook = 'impact_speed' | 'precision' | 'spell_power' | 'channel_rate';

export const SCALING_HOOKS: readonly ScalingHook[] = ['impact_speed', 'precision', 'spell_power', 'channel_rate'];

/** Authored replication semantics for persistent attacks; never inferred from speed. */
export type AttackReplicationMode = 'DETERMINISTIC' | 'CORRECTED' | 'STATEFUL';

/**
 * Per-pierce damage falloff for an unlimited-pierce projectile (2026-09-10
 * encounter-density-policy decision 10, Phase 4b). Opt-in and skill-scoped:
 * setting this on a `projectile` delivery makes it pierce every enemy in its
 * flight corridor (bounded only by `rangeM`/lifetime, not by `maxPierce`),
 * with the Nth enemy struck (0-indexed) taking `rate ** N` of the leaf's
 * damage. Omitting it leaves the delivery exactly as before this field
 * existed — the global `maxPierce ?? 1` default is never flipped by this
 * feature; basic-attack/weapon-primary projectiles and all pre-existing
 * content stay non-piercing.
 *
 * `rate` is `PROVISIONAL_UNCALIBRATED` (same pattern as the ⑦ non-damage
 * placeholder authority): a defensible geometric-decay parameter, not a
 * ratified balance number — calibration is deferred to a follow-up pass.
 */
export interface PierceFalloff {
  /** Geometric decay per pierce ordinal, rate ∈ (0,1). PROVISIONAL_UNCALIBRATED. */
  rate: number;
}

/** A persistent area's pulse and collision-check interval when it declares none: the runtime's and the price's. */
export const DEFAULT_AREA_INTERVAL_S = 0.1;

/**
 * How the skill reaches its target(s). Determines P_valid / P_geom and E[N].
 *
 * AREA DELIVERIES CARRY NO TARGET CAP. How many enemies an area skill reaches
 * is its swept AREA against the encounter density — `E[N]` — and nothing else.
 * A disc, a sector, and a rectangle of the same area are worth the same, and a
 * skill made wider is priced wider rather than being clipped back to an
 * integer ceiling. The former `targetCap` was a hard cap that both clipped
 * appraisal's E[N] and truncated the runtime's struck set, so widening a skill
 * past the cap was free; it is deleted, not defaulted.
 */
export type Delivery =
  | { type: 'self' }
  | { type: 'targeted_ally' }
  | { type: 'targeted_enemy'; rangeM?: number }
  | { type: 'projectile'; rangeM: number; speedMps: number; radiusM: number; replicationMode?: AttackReplicationMode; maxPierce?: number; pierceFalloff?: PierceFalloff }
  | {
      type: 'ground_aoe'; radiusM: number; durationS?: number; pulseIntervalS?: number; collisionCheckIntervalS?: number; targetPolicy?: 'SNAPSHOT_POINT' | 'KEEP_TARGET_HANDLE' | 'REQUERY_AT_EXECUTION';
      /**
       * `'CASTER'`: the circle is centred on the caster's own release point
       * (a canonical `CASTER` placement), never on an enemy — no target is
       * acquired, no aim error is drawn, and there is no guaranteed primary.
       * Absent: the circle is placed at a resolved goal / reacquired target,
       * as before. Only an instant circle carries it; a caster-following
       * persistent or sustained circle has no runtime and is refused at
       * lowering (`CASTER_CIRCLE_LIFECYCLE_RUNTIME_UNIMPLEMENTED`).
       */
      center?: 'CASTER';
    }
  | { type: 'cone'; rangeM: number; angleDeg: number };

/**
 * A resource cost (mana/energy/hp).
 *
 * By default `amount` is paid **once at cast**. When `perSecond` is true this
 * is an **upkeep** cost: `amount` is charged for every second the skill is
 * sustained (gap "D" axis L; see
 * `docs/superpowers/specs/2026-09-08-sustained-caster-coupled-skills.md`).
 * Upkeep is only meaningful on a `SUSTAINED` skill (one carrying `sustain`);
 * `src/safety.ts` validates that pairing. Legacy skills omit `perSecond` and
 * are unchanged.
 */
export interface Cost {
  resource: ResourceKind;
  amount: number;
  /** When true, `amount` is charged per second of sustain, not once at cast. */
  perSecond?: boolean;
}

/**
 * How a `SUSTAINED` skill's active window ends (gap "D" axis L).
 * - `CASTER_RELEASE` — a channel: the caster holds it, and it also cancels on
 *   the caster moving (the current `RuntimeChannel` move-cancel policy).
 * - `TOGGLE_OFF` — a toggle/upkeep aura: stays on until explicitly toggled off
 *   or the resource is depleted; not cancelled by movement.
 * Both are additionally bounded by `maxSustainS` and by upkeep vs. the
 * resource pool (pricing model P-b), so the value envelope is always finite.
 */
export type SustainEndPolicy = 'CASTER_RELEASE' | 'TOGGLE_OFF';

/**
 * Runtime-facing marker for a sustained (caster-coupled-lifetime) skill.
 * The compiler lowers a `CanonicalLifecycleV2` `SUSTAINED` variant to this on
 * the legacy `Skill`; the simulator drives such a skill through
 * `src/sim/channel.ts` (`RuntimeChannel`) instead of a one-shot execution.
 * Position-following (aura/trail, axis P) is explicitly NOT modeled here.
 */
export interface SustainSpec {
  /** Mandatory safety ceiling in seconds — guarantees a finite envelope even at zero upkeep. */
  readonly maxSustainS: number;
  /** Pulse period; each pulse re-applies the skill's effect list. */
  readonly hitIntervalS: number;
  /** How the caster ends it; also selects the move-cancel policy (see `SustainEndPolicy`). */
  readonly end: SustainEndPolicy;
}

/** A `nearby_enemies` selector's radius when it declares none: the runtime's and the price's. */
export const DEFAULT_NEARBY_ENEMIES_RADIUS_M = 2.5;

/** How far a `nearest_n` selector looks for its `n` enemies (m): the runtime's search domain and the price's. */
export const NEAREST_N_SEARCH_DOMAIN_M = 999;

/** Target selector for triggered / conditional sub-effects. */
export type Selector =
  | { type: 'primary' }
  | { type: 'self' }
  | { type: 'nearby_enemies'; radiusM?: number; cap?: number }
  | { type: 'nearest_n'; n: number }
  | { type: 'lowest_hp_ally' };

// ---------------------------------------------------------------------------
// TARGET.1 — Activation & Targeting canonical schema
// (docs/gameplay/skill-activation-targeting-contract.md, `skill-activation-targeting-v1`)
//
// These fields are additive on `Skill` and orthogonal to `Delivery`:
//   Activation != Targeting != Delivery != Effect
// A skill with `targeting` absent takes the pre-existing legacy behavior
// (Delivery-driven target resolution at execute time) unchanged; see
// docs/archive/gameplay/audits/phase-target1-activation-targeting-audit.md §H.
// ---------------------------------------------------------------------------

/** When / by whom the cast begins. Contract §2. */
export type ActivationMode = 'AUTO' | 'MANUAL';

export const ACTIVATION_MODES: readonly ActivationMode[] = ['AUTO', 'MANUAL'];

/** AUTO_TARGET's selection policy. Contract §13. */
export type AutoTargetSelector = 'NEAREST' | 'LOWEST_HP' | 'LOWEST_HP_FRACTION' | 'RANDOM';

export const AUTO_TARGET_SELECTORS: readonly AutoTargetSelector[] = [
  'NEAREST',
  'LOWEST_HP',
  'LOWEST_HP_FRACTION',
  'RANDOM',
];

/** What direction/actor/point the cast is aimed at, decided at cast start. Contract §3. */
export type TargetingSpec =
  | { type: 'MOVE_DIRECTION' }
  | { type: 'AUTO_TARGET'; selector: AutoTargetSelector }
  | { type: 'RANDOM_TARGET' }
  | { type: 'RANDOM_POINT' }
  | { type: 'AIM_DIRECTION' }
  | { type: 'EXPLICIT_TARGET' }
  | { type: 'EXPLICIT_POINT' };

export const TARGETING_SPEC_TYPES: readonly TargetingSpec['type'][] = [
  'MOVE_DIRECTION',
  'AUTO_TARGET',
  'RANDOM_TARGET',
  'RANDOM_POINT',
  'AIM_DIRECTION',
  'EXPLICIT_TARGET',
  'EXPLICIT_POINT',
];

/** AUTO-compatible targeting kinds. Contract §3.1, §98. */
export const AUTO_COMPATIBLE_TARGETING_TYPES: readonly TargetingSpec['type'][] = [
  'MOVE_DIRECTION',
  'AUTO_TARGET',
  'RANDOM_TARGET',
  'RANDOM_POINT',
];

/** MANUAL-compatible targeting kinds. Contract §3.2, §98. */
export const MANUAL_COMPATIBLE_TARGETING_TYPES: readonly TargetingSpec['type'][] = [
  'AIM_DIRECTION',
  'EXPLICIT_TARGET',
  'EXPLICIT_POINT',
];

/**
 * Post-lock invalid-target behavior. Contract §10. Default is `NONE`
 * (fizzle, no implicit substitution, no refund). `ON_TARGET_INVALID` reruns
 * the *same* targeting policy once the locked target becomes invalid — this
 * is distinct from homing/tracking (following the same handle).
 */
export type RetargetPolicy = 'NONE' | 'ON_TARGET_INVALID';

export const RETARGET_POLICIES: readonly RetargetPolicy[] = ['NONE', 'ON_TARGET_INVALID'];

/** A predicate for a `condition` node. Probability is looked up from context. */
export interface Predicate {
  /** target has a given status tag, e.g. "burn". */
  hasTag?: string;
  /** target current-HP fraction is below this (0..1). */
  hpBelow?: number;
  /** logical OR of clauses; complexity counts each clause. */
  or?: Predicate[];
  /** logical AND of clauses; complexity counts each clause. */
  and?: Predicate[];
  /** logical negation. */
  not?: Predicate;
}

// ---------------------------------------------------------------------------
// Effect nodes (leaves) + structural nodes (condition / trigger).
// ---------------------------------------------------------------------------

/**
 * ACTIVE.1 — the source-relative F/K damage magnitude authority
 * (active-skill-mechanics-addendum-v0.1.md §1: `D_core = F × H_base + K × H_stat`).
 * `F`/`K` are canonical skill potency dimensions; they never read
 * STR/AGI/TEC/VIT/MAG/WIL directly (§32.1) and are realized only against the
 * frozen per-activation `SourceSnapshot` (src/sim/source-profile.ts).
 */
export interface SourceRelativeDamage {
  f: number;
  k: number;
}

/**
 * Exactly one damage-magnitude authority:
 * `ABSOLUTE(amount)` or `SOURCE_RELATIVE(F, K)`, never both, never neither.
 * `amount` is optional precisely so a source-relative effect never needs a
 * meaningless `amount: 0` placeholder (STOP §93/§1019); `src/safety.ts`
 * validates the presence-based XOR at authoring time.
 */
export interface DamageEffect {
  kind: 'damage';
  amount?: number;
  /** ACTIVE.1 source-relative authority; see `SourceRelativeDamage`. Mutually
   * exclusive with `amount` — presence establishes authority (work
   * instruction §118-126), never a derived condition like `amount === 0`. */
  sourceRelative?: SourceRelativeDamage;
  damageType?: MechanicalDamageType;
  /** Mitigation-bypass axis, orthogonal to `damageType` (see `MechanicalDamageType` doc comment).
   * Defaults to false; independent of which (if any) mechanical type is set. */
  trueDamage?: boolean;
  /** V2 contact-region policy; no new damage coefficient is authored here. */
  contactMagnitudePolicy?: 'UNMODIFIED' | 'INHERIT_WEAPON_REGION';
  /** §32.2 Derived/runtime relationship this effect's magnitude declares — see `ScalingHook`. */
  scalingHooks?: readonly ScalingHook[];
}

export interface DotEffect {
  kind: 'dot';
  tag?: string;
  /** Compatibility default is physical; authored elemental DoTs should be explicit. */
  damageType?: MechanicalDamageType;
  /** Per-tick magnitude: `ABSOLUTE(amountPerTick)` or `SOURCE_RELATIVE(sourceRelative)`, never both, never neither — the same exclusive authority as `DamageEffect`. */
  amountPerTick?: number;
  /** Per-tick F/K: resolved at the cast's `SourceSnapshot` and Mastery like a damage leaf's. Mutually exclusive with `amountPerTick`. */
  sourceRelative?: SourceRelativeDamage;
  tickS: number;
  durationS: number;
  /** V2 contact-region policy; no new damage coefficient is authored here. */
  contactMagnitudePolicy?: 'UNMODIFIED' | 'INHERIT_WEAPON_REGION';
  /**
   * The skill status this effect lives and dies with (`StatusEffect`): it is applied only to a target that carries
   * that status from this cast, and ends when the status does. Only a composite stage can name one.
   */
  while?: string;
  scalingHooks?: readonly ScalingHook[];
}

export interface HealEffect {
  kind: 'heal';
  amount: number;
  scalingHooks?: readonly ScalingHook[];
}

export interface HotEffect {
  kind: 'hot';
  amountPerTick: number;
  tickS: number;
  durationS: number;
  scalingHooks?: readonly ScalingHook[];
}

export interface ShieldEffect {
  kind: 'shield';
  amount: number;
  durationS: number;
  scalingHooks?: readonly ScalingHook[];
}

export interface HealReductionEffect {
  kind: 'heal_reduction';
  fraction: number; // 0..1
  durationS: number;
  requiresTag?: string;
  scalingHooks?: readonly ScalingHook[];
}

export interface DamageAmpEffect {
  kind: 'damage_amp';
  fraction: number;
  durationS: number;
  /** 'dealt' = target takes more; 'self' = you deal more. Priced the same in v0. */
  mode?: 'dealt' | 'self';
  scalingHooks?: readonly ScalingHook[];
}

export interface DamageReductionEffect {
  kind: 'damage_reduction';
  fraction: number;
  durationS: number;
  scalingHooks?: readonly ScalingHook[];
}

export interface MoveSpeedBuffEffect {
  kind: 'move_speed_buff';
  fraction: number;
  durationS: number;
  scalingHooks?: readonly ScalingHook[];
}

export interface SlowEffect {
  kind: 'slow';
  fraction: number; // 0..1, capped at runtime
  durationS: number;
  /** See `DotEffect.while`. */
  while?: string;
  scalingHooks?: readonly ScalingHook[];
}

export interface StunEffect {
  kind: 'stun';
  durationS: number;
  /** See `DotEffect.while`. */
  while?: string;
  scalingHooks?: readonly ScalingHook[];
}

export interface RootEffect {
  kind: 'root';
  durationS: number;
  /** See `DotEffect.while`. */
  while?: string;
  scalingHooks?: readonly ScalingHook[];
}

/** Denies starting a new cast and interrupts a cast in progress. Does not
 * deny basic attack or movement — pair with `disarm`/`root` for a wider lock. */
export interface SilenceEffect {
  kind: 'silence';
  durationS: number;
  /** See `DotEffect.while`. */
  while?: string;
  scalingHooks?: readonly ScalingHook[];
}

/** Denies starting/continuing a basic attack. Does not deny casting or
 * movement — pair with `silence`/`root` for a wider lock. */
export interface DisarmEffect {
  kind: 'disarm';
  durationS: number;
  /** See `DotEffect.while`. */
  while?: string;
  scalingHooks?: readonly ScalingHook[];
}

/** Where a displacement is measured from: the attack unit's own origin, or the caster. */
export type DisplacementReference = 'anchor' | 'me';

/** Move struck enemies directly away from `from`: the attack unit's anchor
 * (default) or the caster. */
export interface KnockbackEffect {
  kind: 'knockback';
  distanceM: number;
  from?: DisplacementReference;
  scalingHooks?: readonly ScalingHook[];
}

/** Move struck enemies toward `to`: the caster (default) or the attack unit's anchor. */
export interface PullEffect {
  kind: 'pull';
  distanceM: number;
  to?: DisplacementReference;
  scalingHooks?: readonly ScalingHook[];
}

/** Reposition the caster relative to the nearest enemy. */
export interface DashEffect {
  kind: 'dash';
  distanceM: number;
  direction?: 'away_from_nearest' | 'toward_nearest';
  scalingHooks?: readonly ScalingHook[];
}

/** Remove up to `count` harmful statuses from the recipient. */
export interface CleanseEffect {
  kind: 'cleanse';
  count: number;
  scalingHooks?: readonly ScalingHook[];
}

/** Reduce another skill's remaining cooldown/recharge timer. */
export interface CooldownRefundEffect {
  kind: 'cooldown_refund';
  targetSkillId: string;
  seconds: number;
  scalingHooks?: readonly ScalingHook[];
}

/** Make another non-charge skill immediately ready. */
export interface CooldownResetEffect {
  kind: 'cooldown_reset';
  targetSkillId: string;
  scalingHooks?: readonly ScalingHook[];
}

/** Restore charges to another skill, capped by that skill's maximum. */
export interface ChargeGainEffect {
  kind: 'charge_gain';
  targetSkillId: string;
  amount: number;
  scalingHooks?: readonly ScalingHook[];
}

export interface ResourceChangeEffect {
  kind: 'resource_change';
  resource: ResourceKind;
  /** +gain / -drain applied to the target's resource. */
  amount: number;
  scalingHooks?: readonly ScalingHook[];
}

export interface ConditionNode {
  kind: 'condition';
  predicate: Predicate;
  children: EffectNode[];
}

/**
 * `on_projectile_expire` fires when a projectile-delivered attack ends its
 * flight without having struck anything — max travel reached, or the flight
 * cut short by terrain. It is mutually exclusive with `on_hit` for the same
 * delivery: a projectile that contacts a target fires `on_hit` and never
 * `on_projectile_expire`. Its origin is the projectile's final position, so
 * a follow-up `secondary_attack` detonates there rather than at the caster.
 * On a non-projectile delivery the event never fires.
 */
export type TriggerEvent = 'on_hit' | 'on_kill' | 'on_expire' | 'on_apply' | 'on_terrain_collision' | 'on_projectile_expire';

export interface TriggerNode {
  kind: 'trigger';
  event: TriggerEvent;
  selector?: Selector;
  children: EffectNode[];
}

/**
 * A real follow-up attack with its own delivery, collision and HitEvent.
 * Unlike an `on_hit` payload, this performs a new hit resolution. Secondary
 * attacks deal damage/apply statuses normally, but do not propagate `on_hit`
 * procs unless explicitly opted in.
 */
export interface SecondaryAttackNode {
  kind: 'secondary_attack';
  delivery: Delivery;
  procPolicy?: 'blocked' | 'allowed';
  effects: EffectNode[];
}

/**
 * Adds one stack of this cast's mark to each struck enemy that is still alive after the
 * stage's damage. The mark has no magnitude and no duration of its own: it is kept per
 * (cast, `markId`) until the cast's detonation stage consumes it, or the cast ends without one.
 */
export interface MarkEffect {
  kind: 'mark';
  markId: string;
  /** A mark carries no magnitude to scale; present only so every leaf can be asked for its hooks. */
  scalingHooks?: undefined;
}

/**
 * Creates, stacks or refreshes this cast's skill status `name` on each struck enemy that is still alive. What the
 * status is (its lifetime, stacks, timer) is the composite skill's `statuses` entry of that name; the leaf carries
 * no magnitude of its own.
 */
export interface StatusEffect {
  kind: 'status';
  name: string;
  /** A status carries no magnitude to scale; present only so every leaf can be asked for its hooks. */
  scalingHooks?: undefined;
}

/** A name in the element-package registry (`src/sim/element-package.ts`); each package spec adds its own. */
export type ElementStatusName = string;

/**
 * Applies the element status `name` to each struck enemy that is still alive, at `potency`: the fraction of every
 * magnitude the status's stage can reach that this application buys (continuous; 0 applies nothing). Its effects, its
 * duration and what a hit of the owner's can make it do are the package's, realized once from the cast's frozen facts.
 */
export interface ElementStatusEffect {
  kind: 'element_status';
  name: ElementStatusName;
  potency: number;
  /** What evaluation realization resolved the package's source-relative magnitudes to; never part of a canonical skill. */
  realized?: import('./sim/element-package.js').ElementProvenance;
  /** Pricing only, never canonical: the factor a pricing representative scales this status's activation by, as it scales a damage leaf's amount (`unitPricingRatio`). */
  pricingScale?: number;
  /** An element status states its magnitudes through `potency`; present only so every leaf can be asked for its hooks. */
  scalingHooks?: undefined;
}

export type LeafEffect = (
  | DamageEffect
  | DotEffect
  | HealEffect
  | HotEffect
  | ShieldEffect
  | HealReductionEffect
  | DamageAmpEffect
  | DamageReductionEffect
  | MoveSpeedBuffEffect
  | SlowEffect
  | StunEffect
  | RootEffect
  | SilenceEffect
  | DisarmEffect
  | KnockbackEffect
  | PullEffect
  | DashEffect
  | CleanseEffect
  | CooldownRefundEffect
  | CooldownResetEffect
  | ChargeGainEffect
  | ResourceChangeEffect
  | MarkEffect
  | StatusEffect
  | ElementStatusEffect
) & { /** Pricing representatives scale contact counts without changing runtime magnitudes. */ pricingScale?: number };

export type EffectNode = LeafEffect | ConditionNode | TriggerNode | SecondaryAttackNode;

export type EffectKind = EffectNode['kind'];

/**
 * The structural skill shape. This is *not* the engine's canonical unit —
 * `CanonicalSkillV2` is, and appraisal/execution enter through the
 * `src/skill-engine` facade. Three distinct roles share this shape, and each
 * has a role-named alias at its own boundary: authored V1 input
 * (`LegacySkillV1`), the base the canonical contract narrows
 * (`CanonicalSkillV2Input = Omit<Skill, 'delivery' | 'effects'> & ...`), and
 * the compiled execution form (`RuntimeSkill`). Prefer the alias that names
 * the role you mean; this bare spelling means "the shared field set".
 */
export interface Skill {
  id: string;
  name?: string;
  /** Presentation-only lookup key; combat rules never branch on this value. */
  visualProfileId?: string;
  /**
   * ACTIVE.1: optional precisely so a source-relative-timed skill never
   * needs a meaningless `cooldownS: 0` placeholder (STOP §1244/§1830-1839).
   * Exactly one of `cooldownS` (legacy ABSOLUTE authority) or
   * `sourceRelativeTiming` (SOURCE_RELATIVE A/C authority) must be present —
   * `src/safety.ts` validates the presence-based XOR.
   */
  cooldownS?: number;
  castTimeS?: number;
  recoveryS?: number;
  /** Movement speed multiplier during startup; 1=free, 0=immobile. */
  castMoveSpeedMultiplier?: number;
  /** Movement speed multiplier during post-release recovery. */
  recoveryMoveSpeedMultiplier?: number;
  charges?: number;
  /** Sequential recharge time for one charge; defaults to cooldownS. */
  chargeRechargeS?: number;
  costs?: Cost[];
  delivery: Delivery;
  /**
   * Gap "D" axis L — present iff this skill is sustained (channel / toggle /
   * upkeep). The simulator drives a skill carrying `sustain` through
   * `src/sim/channel.ts` rather than executing it once. `src/safety.ts`
   * validates that any `perSecond` cost appears only alongside `sustain`.
   */
  sustain?: SustainSpec;
  effects: EffectNode[];
  /**
   * TARGET.1 canonical activation/targeting (contract `skill-activation-targeting-v1`).
   * Additive and optional: absent means legacy Delivery-driven behavior,
   * unchanged from pre-TARGET.1 (audit §H). When `targeting` is present it is
   * the sole target-acquisition authority — `delivery.type` is never used to
   * re-decide who/where the cast is aimed at.
   */
  activation?: ActivationMode;
  targeting?: TargetingSpec;
  /** Only meaningful when `targeting` resolves to an actor handle. Default: `NONE`. */
  retargetPolicy?: RetargetPolicy;
  /**
   * ACTIVE.1: legal equipped-source families for this skill. Required
   * whenever `sourceRelativeTiming` is set, or any `damage` leaf in
   * `effects` carries `sourceRelative` (src/safety.ts validates this).
   * Independent of `targeting`/`activation` — TARGET.1 target identity and
   * ACTIVE.1 source identity never merge.
   */
  sourceRequirement?: SourceRequirement;
  /** ACTIVE.1 source-relative A/C timing authority; see `SourceRelativeTiming`. */
  sourceRelativeTiming?: SourceRelativeTiming;
  /**
   * SOURCE.2 — canonical magic-influence metadata (source-weapon-technique-hybrid-contract.md
   * §23). Required exactly when `sourceRequirement.sourceClass === 'MAGIC'`;
   * must be absent otherwise (`src/safety.ts` validates this XOR).
   */
  magicInfluence?: MagicInfluence;
  /**
   * MASTERY.1 — canonical owner-scoped Mastery realization metadata for a
   * supported canonical direct-damage Skill. Additive/optional: a Skill
   * without this field remains compatibility-M10 (legacy/no-policy path,
   * see src/sim/mastery.ts). Immutable authored data; never rewritten by
   * owner Mastery-level changes. `src/generation/numeric/canonical-verify.ts`
   * verifies its integrity; `src/safety.ts` validates its structural shape.
   */
  masteryPolicy?: SkillMasteryPolicyV1;
  /** Plan `sparkling-purring-allen` Stage 3 — see `DirectDamageAuthoringProvenanceV1`. Present only for a canonical direct-damage skill authored against a crafter's live snapshot. */
  authoringProvenance?: DirectDamageAuthoringProvenanceV1;
  /** See `PlaceholderNonDamageComponentEvidence` — NOT balance-authoritative. Present only when >=1 effect was sized by the bootstrap placeholder path. */
  nonDamageAuthoringProvenance?: readonly PlaceholderNonDamageComponentEvidence[];
}

export const LEAF_EFFECT_KINDS: readonly EffectKind[] = [
  'damage',
  'dot',
  'heal',
  'hot',
  'shield',
  'heal_reduction',
  'damage_amp',
  'damage_reduction',
  'move_speed_buff',
  'slow',
  'stun',
  'root',
  'silence',
  'disarm',
  'knockback',
  'pull',
  'dash',
  'cleanse',
  'cooldown_refund',
  'cooldown_reset',
  'charge_gain',
  'resource_change',
  'mark',
  'status',
  'element_status',
];

export function isLeaf(node: EffectNode): node is LeafEffect {
  return node.kind !== 'condition' && node.kind !== 'trigger' && node.kind !== 'secondary_attack';
}
